import { useTranslation } from "react-i18next";
import { FormSectionComponent } from "../FormSectionComponent";
import InfoTooltip from "../../../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { FlexVerticalAligned } from "../../../../../../../common/components/styled/styled";
import { Box, CircularProgress, Link, Switch, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  getWorkAgreementDocumentInfo,
  getWorkAgreementDocumentDownloadUrls,
} from "../../../../../orderRowsDetailedSlice";
import { useAppDispatch } from "../../../../../../../app/hooks";
import { isFulfilled } from "@reduxjs/toolkit";
import {
  decodeWorkAgreementDocumentDownloadUrls,
  FormKeys,
} from "../../helpers";
import {
  WorkAgreementDocumentInfo,
  WorkAgreementDocumentSourceOptions,
} from "../../../../../orderAPI";
import { format } from "date-fns";
import { leadingZerosDateFormat } from "../../../../../../../common/helpers/formatDateTime";
import { downloadBlob } from "../../../../../../../common/helpers/downloadBlob";
import {
  WorkAgreementDocumentDialog,
  WorkAgreementDocumentDialogResult,
} from "./WorkAgreementDocumentDialog";

type Props = {
  talentId: string;
  isReadOnly: boolean;
  titleWidth?: number;
  contentWidth?: number;
  setHasSystemGeneratedDocument?: (hasSystemGeneratedDocument: boolean) => void;
};

type WorkAgreementDocumentFileLinkProps = {
  filename: string;
  workAgreementDocumentId?: string;
  fileKey?: "mainDocument" | "attachment";
  attachmentIndex?: number;
};

const WorkAgreementDocumentFileLink = ({
  filename,
  workAgreementDocumentId,
  fileKey,
  attachmentIndex,
}: WorkAgreementDocumentFileLinkProps) => {
  const dispatch = useAppDispatch();
  const [isDownloading, setIsDownloading] = useState(false);
  const isDownloadable = !!workAgreementDocumentId && !!fileKey;

  const handleDownload = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!workAgreementDocumentId || !fileKey || isDownloading) return;
    setIsDownloading(true);
    try {
      const action = await dispatch(
        getWorkAgreementDocumentDownloadUrls({ workAgreementDocumentId }),
      );
      if (!isFulfilled(action)) return;
      const decoded = decodeWorkAgreementDocumentDownloadUrls(action.payload);
      const downloadUrl =
        fileKey === "mainDocument"
          ? decoded.mainDocument?.url
          : decoded.attachments[attachmentIndex ?? 0]?.url ??
            decoded.attachments.find(
              (attachment) => attachment.filename === filename,
            )?.url;
      if (!downloadUrl) return;
      const response = await fetch(downloadUrl);
      if (!response.ok) return;
      downloadBlob(await response.blob(), filename);
    } catch {
      return;
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isDownloadable) {
    return <Typography variant="body2">{filename}</Typography>;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {isDownloading ? (
        <CircularProgress size={16} />
      ) : (
        <Link
          component="button"
          type="button"
          onClick={handleDownload}
          variant="body2"
          underline="hover"
          sx={{
            cursor: "pointer",
            verticalAlign: "baseline",
            font: "inherit",
            textAlign: "left",
          }}
        >
          {filename}
        </Link>
      )}
    </Box>
  );
};

export const WorkAgreementDocument = ({
  talentId,
  isReadOnly,
  titleWidth,
  contentWidth,
  setHasSystemGeneratedDocument,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { watch, setValue } = useFormContext();
  const workAgreementDocumentId = watch(FormKeys.WorkAgreementDocumentId);
  const [checked, setChecked] = useState(!!workAgreementDocumentId);

  const [workAgreementDocument, setWorkAgreementDocument] =
    useState<File | null>(null);
  const [additionalDocuments, setAdditionalDocuments] = useState<File[]>([]);
  const [originalSigningDate, setOriginalSigningDate] = useState<Date | null>(
    null,
  );
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [documentInfo, setDocumentInfo] =
    useState<WorkAgreementDocumentInfo | null>(null);

  const clearUploadedDocument = () => {
    setWorkAgreementDocument(null);
    setAdditionalDocuments([]);
    setOriginalSigningDate(null);
    setIsSaved(false);
    setDocumentInfo(null);
    setValue(FormKeys.WorkAgreementDocumentId, null);
  };

  const closeDialog = () => {
    if (isSaving) return;
    setIsDialogOpen(false);
    setChecked(isSaved);
    if (!isSaved) {
      clearUploadedDocument();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isSaving) return;
    setChecked(event.target.checked);
    if (event.target.checked) {
      setIsDialogOpen(true);
    } else {
      setIsDialogOpen(false);
      clearUploadedDocument();
    }
  };

  const handleSaved = (result: WorkAgreementDocumentDialogResult) => {
    setWorkAgreementDocument(result.workAgreementDocument);
    setAdditionalDocuments(result.additionalDocuments);
    setOriginalSigningDate(result.originalSigningDate);
    setIsSaved(true);
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (workAgreementDocumentId) {
      setChecked(true);
    }
  }, [workAgreementDocumentId]);

  useEffect(() => {
    if (!workAgreementDocumentId || !isReadOnly) return;
    setIsLoading(true);
    dispatch(getWorkAgreementDocumentInfo({ workAgreementDocumentId }))
      .then((action) => {
        if (!isFulfilled(action)) {
          return;
        }
        setDocumentInfo(action.payload);
        if (action.payload.signedAt != null) {
          setOriginalSigningDate(new Date(action.payload.signedAt));
        }
        if (
          action.payload.source ===
          WorkAgreementDocumentSourceOptions.SYSTEM_GENERATE
        ) {
          setChecked(false);
          setHasSystemGeneratedDocument && setHasSystemGeneratedDocument(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    workAgreementDocumentId,
    isReadOnly,
    dispatch,
    setHasSystemGeneratedDocument,
  ]);

  const rows = [
    {
      title: "",
      component: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Switch
            checked={checked}
            onChange={handleChange}
            disabled={isReadOnly}
          />
          {(isSaved || isReadOnly) && checked && !isDialogOpen && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {(workAgreementDocument ||
                documentInfo?.workAgreementDocument) && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
                >
                  <Typography variant="secondary">
                    {t("order:replacementWorkAgreementTitle")}
                  </Typography>
                  <WorkAgreementDocumentFileLink
                    filename={
                      workAgreementDocument?.name ??
                      documentInfo?.workAgreementDocument ??
                      ""
                    }
                    workAgreementDocumentId={
                      isReadOnly ? workAgreementDocumentId : undefined
                    }
                    fileKey="mainDocument"
                  />
                </Box>
              )}
              {(additionalDocuments.length > 0 ||
                (documentInfo?.attachments?.length ?? 0) > 0) && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
                >
                  <Typography variant="secondary">
                    {t("order:additionalAttachmentsTitle")}
                  </Typography>
                  {additionalDocuments.length > 0
                    ? additionalDocuments.map((document, index) => (
                        <WorkAgreementDocumentFileLink
                          key={`${document.name}-${index}`}
                          filename={document.name}
                        />
                      ))
                    : (documentInfo?.attachments ?? []).map(
                        (filename, index) => (
                          <WorkAgreementDocumentFileLink
                            key={`${filename}-${index}`}
                            filename={filename}
                            workAgreementDocumentId={workAgreementDocumentId}
                            fileKey="attachment"
                            attachmentIndex={index}
                          />
                        ),
                      )}
                </Box>
              )}
              {originalSigningDate && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
                >
                  <Typography variant="secondary">
                    {t("order:workAgreementSigningDateTitle")}
                  </Typography>
                  <Typography variant="body2">
                    {format(originalSigningDate, leadingZerosDateFormat)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ),
    },
  ];

  const loadingRows = [
    {
      title: "",
      component: <CircularProgress />,
    },
  ];

  return (
    <>
      <FormSectionComponent
        title={t("order:workAgreementDocument")}
        rows={isLoading ? loadingRows : rows}
        infoTooltip={
          <FlexVerticalAligned ml="8px">
            <InfoTooltip title={t("order:workAgreementDocumentTooltip")} />
          </FlexVerticalAligned>
        }
        titleWidth={titleWidth}
        contentWidth={contentWidth}
      />
      <WorkAgreementDocumentDialog
        open={isDialogOpen}
        talentId={talentId}
        initialWorkAgreementDocument={workAgreementDocument}
        initialAdditionalDocuments={additionalDocuments}
        initialSigningDate={originalSigningDate}
        onCancel={closeDialog}
        onSaved={handleSaved}
        onSavingChange={setIsSaving}
      />
    </>
  );
};
